import React, { useMemo, useRef, useState } from "react";
import type { ElevenVoice } from "../../api";

type Props = {
  own: ElevenVoice[];
  library: ElevenVoice[];
  favorites: Set<string>;
  value: string | null;
  onChange: (voiceId: string) => void;
  onToggleFavorite: (voiceId: string) => void;
  disabled?: boolean;
};

/**
 * Stimmen-Auswahl für die ElevenLabs-Vertonung. Gruppiert „Meine Stimmen"
 * (geklont/professionell) und die „Bibliothek", mit Suche, Preview-Play
 * (preview_url) und Favoriten-Stern. Favoriten erscheinen oben.
 */
export const VoicePicker: React.FC<Props> = ({
  own,
  library,
  favorites,
  value,
  onChange,
  onToggleFavorite,
  disabled,
}) => {
  const [q, setQ] = useState("");
  const [playing, setPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const preview = (v: ElevenVoice): void => {
    if (!v.previewUrl) return;
    if (!audioRef.current) audioRef.current = new Audio();
    const a = audioRef.current;
    if (playing === v.voiceId) {
      a.pause();
      setPlaying(null);
      return;
    }
    a.pause();
    a.src = v.previewUrl;
    a.currentTime = 0;
    a.volume = 0.8;
    setPlaying(v.voiceId);
    a.play().catch(() => setPlaying(null));
    a.onended = () => setPlaying((p) => (p === v.voiceId ? null : p));
  };

  const filterFn = (v: ElevenVoice): boolean => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return (
      v.name.toLowerCase().includes(needle) ||
      (v.description ?? "").toLowerCase().includes(needle) ||
      Object.values(v.labels ?? {}).some((l) => l.toLowerCase().includes(needle))
    );
  };

  const all = useMemo(() => [...own, ...library], [own, library]);
  const favVoices = useMemo(
    () => all.filter((v) => favorites.has(v.voiceId)).filter(filterFn),
    [all, favorites, q],
  );
  const ownFiltered = useMemo(() => own.filter(filterFn), [own, q]);
  const libFiltered = useMemo(() => library.filter(filterFn), [library, q]);

  const renderVoice = (v: ElevenVoice): React.ReactNode => {
    const isSel = value === v.voiceId;
    const isFav = favorites.has(v.voiceId);
    const isPlaying = playing === v.voiceId;
    return (
      <div key={v.voiceId} className={`voice-row ${isSel ? "selected" : ""}`}>
        <button
          type="button"
          className="voice-row-pick"
          onClick={() => onChange(v.voiceId)}
          disabled={disabled}
          title="Diese Stimme verwenden"
        >
          <span className="voice-row-radio">{isSel ? "●" : "○"}</span>
          <span className="voice-row-name">{v.name}</span>
          {v.labels?.accent && <span className="voice-row-tag">{v.labels.accent}</span>}
          {v.labels?.gender && <span className="voice-row-tag">{v.labels.gender}</span>}
        </button>
        <button
          type="button"
          className={`voice-row-fav ${isFav ? "on" : ""}`}
          onClick={() => onToggleFavorite(v.voiceId)}
          title={isFav ? "Favorit entfernen" : "Als Favorit markieren"}
          aria-pressed={isFav}
        >
          {isFav ? "★" : "☆"}
        </button>
        <button
          type="button"
          className={`voice-row-play ${isPlaying ? "playing" : ""}`}
          onClick={() => preview(v)}
          disabled={!v.previewUrl}
          title={v.previewUrl ? (isPlaying ? "Stop" : "Vorschau") : "Keine Vorschau verfügbar"}
        >
          {isPlaying ? "■" : "▶"}
        </button>
      </div>
    );
  };

  return (
    <div className="voice-picker">
      <input
        type="search"
        className="voice-picker-search"
        placeholder="Stimme suchen…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="voice-picker-list">
        {favVoices.length > 0 && (
          <>
            <div className="voice-picker-group">★ Favoriten</div>
            {favVoices.map(renderVoice)}
          </>
        )}
        <div className="voice-picker-group">Meine Stimmen</div>
        {ownFiltered.length > 0 ? (
          ownFiltered.map(renderVoice)
        ) : (
          <div className="voice-picker-empty">Keine eigenen Stimmen im Account.</div>
        )}
        <div className="voice-picker-group">Bibliothek</div>
        {libFiltered.length > 0 ? (
          libFiltered.map(renderVoice)
        ) : (
          <div className="voice-picker-empty">Keine Treffer.</div>
        )}
      </div>
    </div>
  );
};
